import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package, Users, UserRound, BookOpen, Download, Tag } from 'lucide-react';
import { StockMasterContent } from './StockMaster';
import SupplierDetailsTab from '@/components/SupplierDetailsTab';
import CustomerDetailsTab from '@/components/CustomerDetailsTab';
import ExportDataTab from '@/components/ExportDataTab';
import LabelsTab from '@/components/labels/LabelsTab';
import { productsAPI } from '@/lib/api';
import { useRole } from '@/contexts/RoleContext';

// ── Tab definitions ───────────────────────────────────────────────────────────
const ALL_TABS = [
  { id: 'stock',     label: 'Stock Master',     icon: Package,   ownerOnly: false },
  { id: 'labels',    label: 'Print Labels',     icon: Tag,       ownerOnly: false },
  { id: 'suppliers', label: 'Supplier Details', icon: Users,     ownerOnly: false },
  { id: 'customers', label: 'Customer Details', icon: UserRound, ownerOnly: false },
  { id: 'export',    label: 'Export Data',      icon: Download,  ownerOnly: true  },
];

// ── Main page ─────────────────────────────────────────────────────────────────
const Appendix = () => {
  const location = useLocation();
  const { role } = useRole();
  const isOwner = role === 'owner';
  const TABS = ALL_TABS.filter(t => !t.ownerOnly || isOwner);
  const [activeTab, setActiveTab] = useState(location.state?.tab ?? 'stock');
  const catalogId      = location.state?.catalogId      ?? null;
  const focusSellPrice = location.state?.focusSellPrice ?? false;
  const currentMrp     = location.state?.currentMrp     ?? null;

  const { data: catalogProducts = [] } = useQuery({
    queryKey: ['appendix-catalog-products'],
    queryFn: () => productsAPI.getAll({ limit: 1000 }).then(r => r.data?.items ?? r.data ?? []),
    staleTime: 30_000,
  });
  const newFromPurchaseCount = catalogProducts.filter(
    p => p.source === 'auto_purchase' && (!p.brand || !p.category || !p.hsn_code || !p.barcode)
  ).length;

  return (
    <div className="p-4 lg:p-6 space-y-3">
      {/* Page header */}
      <div className="flex items-center gap-2">
        <BookOpen className="h-6 w-6 text-primary shrink-0" />
        <h1 className="text-2xl font-bold text-foreground">Appendix</h1>
      </div>

      {/* Horizontal tab bar */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(tab => {
          const Icon     = tab.icon;
          const isActive = activeTab === tab.id;
          const badge    = tab.id === 'stock' ? newFromPurchaseCount : 0;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              {badge > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  isActive ? 'bg-primary/20 text-primary' : 'bg-red-100 text-red-700'
                }`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Tab: Stock Master ── */}
      {activeTab === 'stock' && (
        <StockMasterContent
          onNeedRefresh={null}
          newFromPurchaseCount={newFromPurchaseCount}
          initialCatalogId={catalogId}
          autoFocusSellPrice={focusSellPrice}
          currentMrp={currentMrp}
        />
      )}

      {/* ── Tab: Supplier Details ── */}
      {activeTab === 'suppliers' && (
        <SupplierDetailsTab />
      )}

      {/* ── Tab: Customer Details ── */}
      {activeTab === 'customers' && (
        <CustomerDetailsTab />
      )}

      {/* ── Tab: Labels ── */}
      {activeTab === 'labels' && (
        <LabelsTab />
      )}

      {/* ── Tab: Export Data (owner only) ── */}
      {activeTab === 'export' && isOwner && (
        <ExportDataTab />
      )}
    </div>
  );
};

export default Appendix;
